import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api";
import { MobileNav } from "@/components/MobileNav";
import { SponsorStrip } from "@/components/SponsorStrip";
import { MediaPartnerStrip } from "@/components/MediaPartnerStrip";
import { MenuComunicati } from "@/components/MenuComunicati";
import { toast } from "sonner";

export default function Galleria() {
  const [clubs, setClubs] = useState(null);

  useEffect(() => {
    api
      .get("/clubs")
      .then((res) => setClubs(res.data))
      .catch(() => toast.error("Impossibile caricare la galleria"));
  }, []);

  const photos = useMemo(() => {
    if (!clubs) return [];
    const out = [];
    for (const club of clubs) {
      for (const p of club.team_photos || []) {
        const team = (club.teams || []).find((t) => t.name === p.team) || {};
        out.push({
          team: p.team,
          photo: p.photo,
          club: club.name,
          league: team.league,
          girone: team.girone,
          giornata: p.giornata || 1,
        });
      }
    }
    return out;
  }, [clubs]);

  const byGiornata = useMemo(() => {
    const groups = {};
    for (const p of photos) {
      (groups[p.giornata] = groups[p.giornata] || []).push(p);
    }
    return Object.keys(groups)
      .map(Number)
      .sort((a, b) => a - b)
      .map((g) => ({ giornata: g, items: groups[g] }));
  }, [photos]);

  return (
    <div className="bg-[#e3edf6] text-[#0f172a] min-h-screen">
      <nav className="border-b border-[#e2e8f0] px-6 md:px-8 py-5 sticky top-0 bg-[#e3edf6]/90 backdrop-blur-md z-50">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
            <img src="/logo-coppa.png" alt="Coppa dei Club" className="h-8 w-auto" />
            <span className="text-[#0284c7] font-mono-x text-xs">/ Hub</span>
          </Link>
          <div className="flex items-center gap-2 text-xs font-ui text-[#475569]">
            <a href="/#calendari" data-testid="nav-calendari" className="hidden sm:inline-block px-4 py-2 bg-white text-[#0f172a] hover:bg-[#38bdf8] transition-all rounded font-semibold border border-[#cbd5e1]">
              Calendari Gare
            </a>
            <Link to="/circoli" data-testid="nav-circoli" className="hidden sm:inline-block px-4 py-2 bg-white text-[#0f172a] hover:bg-[#38bdf8] transition-all rounded font-semibold border border-[#cbd5e1]">
              Circoli
            </Link>
            <Link to="/galleria" data-testid="nav-galleria" className="hidden sm:inline-block px-4 py-2 bg-white text-[#0284c7] hover:bg-[#38bdf8] hover:text-[#0f172a] transition-all rounded font-semibold border border-[#38bdf8]">
              Galleria foto
            </Link>
            <MenuComunicati prefix={"/"} />
            <Link
              to="/admin"
              data-testid="nav-admin-login"
              className="hidden sm:inline-block px-4 py-2 bg-[#e2e8f0] text-[#0f172a] hover:bg-[#38bdf8] hover:text-[#0f172a] transition-all rounded font-semibold border border-[#cbd5e1]"
            >
              Login Admin
            </Link>
            <MobileNav active="galleria" />
          </div>
        </div>
      </nav>

      <SponsorStrip />

      <MediaPartnerStrip />

      <header className="py-16 md:py-20 px-6 md:px-8 border-b border-[#e2e8f0]">
        <div className="max-w-6xl mx-auto">
          <span className="font-mono-x text-xs text-[#0284c7] tracking-widest uppercase block mb-2">
            Coppa dei Club 2026-27
          </span>
          <h1 className="font-display text-4xl md:text-5xl text-[#0f172a] mb-4">Galleria foto</h1>
          <p className="text-base text-[#475569] max-w-2xl leading-relaxed">
            {clubs
              ? "Le foto ufficiali delle squadre, organizzate per giornata di campionato. Apri una cartella per vedere le foto."
              : "Caricamento della galleria..."}
          </p>
        </div>
      </header>

      <main className="py-14 px-6 md:px-8">
        <div className="max-w-6xl mx-auto">
          {clubs && byGiornata.length === 0 && (
            <p className="text-sm text-[#475569]" data-testid="gallery-empty">
              Nessuna foto disponibile al momento.
            </p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="gallery-folders">
            {byGiornata.map((group) => {
              const cover = group.items[0];
              return (
                <Link
                  key={group.giornata}
                  to={`/galleria/giornata/${group.giornata}`}
                  data-testid={`gallery-folder-${group.giornata}`}
                  className="relative rounded-xl overflow-hidden border border-[#e2e8f0] bg-white group cursor-pointer hover:border-[#38bdf8] transition-colors"
                >
                  <div className="relative aspect-[4/3] overflow-hidden bg-[#dbe8f2]">
                    {cover && (
                      <img
                        src={cover.photo}
                        alt={`${group.giornata}° giornata`}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.04]"
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a]/85 via-[#0f172a]/25 to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 px-5 pb-4 flex items-end justify-between gap-3">
                      <div>
                        <h3 className="text-white font-display text-xl leading-tight">
                          {group.giornata}° giornata
                        </h3>
                        <p className="text-[#bae6fd] text-xs font-ui">{group.items.length} foto</p>
                      </div>
                      <span className="w-9 h-9 rounded-full bg-white/90 text-[#0f172a] flex items-center justify-center shrink-0 group-hover:bg-[#38bdf8] transition-colors">
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                          <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </main>

      <footer className="border-t border-[#e2e8f0] py-6 px-8 bg-[#d6e4f0] text-xs font-mono-x text-[#64748b] text-center">
        Coppa dei Club 2026-27 • Portale Ufficiale Calendari, Gironi Gold &amp; Silver • MSP Italia Umbria • Segreteria
        Generale:{" "}
        <a href="tel:3510262959" className="text-[#0284c7] hover:underline">
          3510262959
        </a>{" "}
        • Segreteria Coppa dei Club:{" "}
        <a href="mailto:coppadeiclubmspumbria@gmail.com" className="text-[#0284c7] hover:underline">
          coppadeiclubmspumbria@gmail.com
        </a>
      </footer>
    </div>
  );
}
